export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '')
}

export function formatPhone(value: string): string {
  const raw = value.trim()
  let digits = digitsOnly(raw)
  if (!raw.startsWith('+') && (digits.length === 10 || digits.length === 11)) digits = `55${digits}`
  digits = digits.slice(0, 13)
  if (!digits) return ''

  let formatted = `+${digits.slice(0, 2)}`
  if (digits.length > 2) {
    formatted += ` (${digits.slice(2, 4)}`
    if (digits.length >= 4) formatted += ')'
  }
  if (digits.length > 4) {
    formatted += ` ${digits.slice(4, 9)}`
    if (digits.length > 9) formatted += `-${digits.slice(9, 13)}`
  }
  return formatted
}

export function normalizePhone(value: string): string | null {
  const raw = value.trim()
  if (!raw) return null
  const digits = digitsOnly(raw)
  if (!raw.startsWith('+') && (digits.length === 10 || digits.length === 11)) return `+55${digits}`
  if (raw.startsWith('+') && digits.length >= 8 && digits.length <= 15) return `+${digits}`
  if (digits.startsWith('55') && digits.length >= 12 && digits.length <= 15) return `+${digits}`
  return null
}

export function formatCpfCnpj(value: string): string {
  const digits = digitsOnly(value).slice(0, 14)
  if (digits.length <= 11) {
    let formatted = digits.slice(0, 3)
    if (digits.length > 3) formatted += `.${digits.slice(3, 6)}`
    if (digits.length > 6) formatted += `.${digits.slice(6, 9)}`
    if (digits.length > 9) formatted += `-${digits.slice(9, 11)}`
    return formatted
  }

  let formatted = digits.slice(0, 2)
  if (digits.length > 2) formatted += `.${digits.slice(2, 5)}`
  if (digits.length > 5) formatted += `.${digits.slice(5, 8)}`
  if (digits.length > 8) formatted += `/${digits.slice(8, 12)}`
  if (digits.length > 12) formatted += `-${digits.slice(12, 14)}`
  return formatted
}

export function normalizeCpfCnpj(value: string): string | null {
  const digits = digitsOnly(value)
  if (digits.length === 11 && isValidCpf(digits)) return digits
  if (digits.length === 14 && isValidCnpj(digits)) return digits
  return null
}

export function formatCep(value: string): string {
  const digits = digitsOnly(value).slice(0, 8)
  if (digits.length <= 5) return digits
  return `${digits.slice(0, 5)}-${digits.slice(5)}`
}

export function normalizeCep(value: string): string | null {
  const digits = digitsOnly(value)
  return digits.length === 8 ? digits : null
}

function isValidCpf(digits: string): boolean {
  if (hasRepeatedDigits(digits)) return false
  const first = calculateDigit(digits.slice(0, 9), 10)
  const second = calculateDigit(digits.slice(0, 10), 11)
  return Number(digits[9]) === first && Number(digits[10]) === second
}

function isValidCnpj(digits: string): boolean {
  if (hasRepeatedDigits(digits)) return false
  const first = calculateDigit(digits.slice(0, 12), 5)
  const second = calculateDigit(digits.slice(0, 13), 6)
  return Number(digits[12]) === first && Number(digits[13]) === second
}

function calculateDigit(digits: string, initialFactor: number): number {
  let factor = initialFactor
  const sum = [...digits].reduce((total, digit) => {
    const next = total + Number(digit) * factor
    factor = factor === 2 ? 9 : factor - 1
    return next
  }, 0)
  const remainder = sum % 11
  return remainder < 2 ? 0 : 11 - remainder
}

function hasRepeatedDigits(value: string): boolean {
  return value.length > 0 && [...value].every((digit) => digit === value[0])
}
